import { ISerializedSuggestionProviderConfiguration } from '@1js/search-box-hostapp-interface';
import * as Init from '@1js/search-page-init';
import { Injectable } from '@angular/core';
import { Dispatcher } from 'flux';
import { loadTheme } from 'office-ui-fabric-react';
import * as React from 'react';
import { merge } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { SearchConfigService } from '../search-results/services/search-config.service';
import { GrayscaleThemeShade, ThemeShade } from '../shared/constants/theme-shade.enum';
import { UserProfileService } from '../shared/instrumentation/user-profile.service';
import { FileItem } from '../shared/models/file-item.model';
import { OfficeDotComOrigin } from '../shared/models/telemetry-constants.model';
import { Workload } from '../shared/models/workload.model';
import { MruDataService } from '../shared/mru/mru-data.service';
import { AccessTokenConsumer } from '../shared/services/access-token.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { EventService } from '../shared/services/event.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { OneDriveGraphService } from '../shared/services/onedrive-graph.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { Theme, ThemeChangeEvent, ThemingService } from '../shared/services/theming.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { SharePointSitesService } from '../sharepoint/sharepoint-sites.service';
import { FastSearchUXInstrumentationService } from './fast-searchux-instrumentation.service';
import { FastSearchUXMainSettingsService } from './fast-searchux-main-settings.service';
import { FilesFastSuggestionProvider } from './fast-suggestion-providers/files.fast-suggestion-provider';
import { SitesFastSuggestionProvider } from './fast-suggestion-providers/sites.fast-suggestion-provider';
import { ThirdPartyAppsFastSuggestionProvider } from './fast-suggestion-providers/third-party-apps.fast-suggestion-provider';
import { SearchInstrumentationService } from './search-instrumentation.service';
import { IMainSettings, LogEvent, Main, SearchConfiguration, SearchDispatcher, SearchPageBootstrapperService } from './search-page-bootstrapper/search-page-bootstrapper.service';
import { SearchPageInstrumentationService } from './search-page-instrumentation/search-page-instrumentation.service';
import { SharedSearchService } from './shared-search.service';

type StringMapLoader = (locale: string) => Promise<{}>;

/**
 * The search service is responsible for loading and configuring Office.com
 * search using SearchUX. SearchUX is made up of two main parts. The init module
 * and the main module. The SearchUX init module is included in the vendor
 * bundle, and the SearchUX main module is loaded lazily. The init module
 * contains the search box, while the main module has the data layer
 * (autosuggest, search results) and the search results page (SERP).
 */
@Injectable({ providedIn: 'root' })
export class FastSearchUXService {
    // Used to load Office.com's custom Search Box configuration.
    public readonly fastSearchBoxConfigurationName = 'officehome';

    // A Token Provider for Bing answers.
    private readonly bingAnswersTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.BingAnswers);

    // A Token Provider for calling MS Graph APIs.
    private readonly graphTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.Graph);

    // A Token Provider for calling Loki APIs.
    private readonly lokiTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.Loki);

    // A Token Provider for calling Thor APIs.
    private readonly thorTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.Thor);

    // A Token Provider for calling ODS
    private readonly odsTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.Ods);

    // A Token Provider for calling Tenant Feedback
    private readonly tenantFeedbackTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.TenantFeedback);

    // A Token Provider for 3S Events API
    private readonly substrateTokenProvider = this.sharedSearchService.createTokenProvider(AccessTokenConsumer.QueryFormulation);

    // This buffer is used to queue FAST Aria events before their Aria module is initialized.
    private readonly fastLogEventBuffer: Array<LogEvent> = [];

    // This is used to disconnect the log buffer from the FAST Dispatcher.
    private fastLogEventBufferUnregisterToken: string;

    // A flag indicating if the FAST main module load has been initiated.
    private loadMainModuleInitiated = false;

    // Variable to keep the StringMapLoader promise to avoid instantiating it multiple
    // times and to be able to resolve the StringMapLoader as early as possible
    private stringMapLoaderPromise?: Promise<StringMapLoader>;

    /**
     * FAST Modules
     */

    // The initial FAST package, used to load up the Search Box.
    public readonly searchUXInitModule = Init;

    // The main FAST package, which contains the SERP and most business logic.
    public readonly searchUXMainModule: Promise<Main> = new Promise(resolve => this.resolveSearchUXMainModule = resolve);
    // Used to resolve the Main module loaded Promise.
    private resolveSearchUXMainModule: (main: Main) => void;

    // FAST package for the Search Dispatcher
    public readonly searchUXDispatcherModule: Promise<SearchDispatcher> = new Promise(resolve => this.resolveSearchUXDispatcherModule = resolve);
    // Used to resolve the Dispatcher module loaded Promise.
    private resolveSearchUXDispatcherModule: (dispatcher: SearchDispatcher) => void;

    // Used to know when to empty the log event buffer after both loggers have copied it
    private emptyLogEventBuffer = false;

    constructor(
        private readonly capabilitesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly deviceDetector: DeviceDetector,
        private readonly eventService: EventService,
        private readonly fastSearchUXInstrumentationService: FastSearchUXInstrumentationService,
        private readonly mruDataService: MruDataService,
        private readonly searchConfigService: SearchConfigService,
        private readonly searchInstrumentationService: SearchInstrumentationService,
        private readonly sharePointSitesService: SharePointSitesService,
        private readonly themingService: ThemingService,
        private readonly workloadsService: WorkloadsService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly userProfileService: UserProfileService,
        private readonly oneDriveGraphService: OneDriveGraphService,
        private readonly mainSettingsService: FastSearchUXMainSettingsService,
        private readonly scheduler: SchedulerService,
        private readonly userContextService: UserContextService,
        private readonly sharedSearchService: SharedSearchService,
        private readonly routeService: OfficeRouteService,
        private readonly searchPageInstrumentationService: SearchPageInstrumentationService,
        private readonly searchPageBootstrapperService: SearchPageBootstrapperService
    ) {
        this.initialize();
    }

    /**
     * Begins fetching the SearchUX main module asynchronously in the
     * background. The SearchUX main module is not included in the Office.com
     * main bundle for performance reasons. The SearchUX main module contains
     * the SERP, the Data Layer, and other components that aren't part of the
     * SearchUX init module. Note that the search box is part of the init
     * module.
     */
    public async loadMainModule() {
        if (this.loadMainModuleInitiated) {
            return;
        }

        this.loadMainModuleInitiated = true;

        (await this.searchPageInstrumentationService.searchUXInstrumenter).mark('SerpConfigStart');

        const searchConfigPromise = this.getSearchConfigPromise();
        searchConfigPromise.then(() => {
            this.searchPageInstrumentationService.searchUXInstrumenter.then(instrumenter => {
                instrumenter.mark('SerpConfigDone');
            });
        });

        searchConfigPromise.then(searchConf => {
            const ecsSettings = searchConf?.ecsSettings;
            //Broadcast the event when searchux general feedback is enabled
            if (ecsSettings?.['searchux.generalFeedback']) {
                this.eventService.broadcastEvent({
                    name: AngularEvents.searchUXGeneralFeedbackEnabled,
                    args: []
                });
            }

            this.eventService.broadcastEvent({
                name: AngularEvents.isContextualRailEnabled,
                args: [ecsSettings?.['searchux.isContextualRailEnabled']]
            });
        });

        if (this.clientStartupConfigService.config.authType !== AuthType.MSA) {
            this.runPrefetchCalls();
        }

        const withBasicMonitor = (await this.sharedSearchService.searchUXPrefetchModule).withBasicMonitor;

        const main =
            await withBasicMonitor(
                {
                    monitorName: 'main_module_load',
                    monitorType: 'generic_qos'
                },
                (() => this.loadMain().then(mainResponse => {
                    mainResponse.setCDNOptions(this.searchPageBootstrapperService.cdnOptions);

                    return mainResponse;
                }))
            )();

        if (!this.isTestEnv()) {
            await main.loadStrings(await this.stringMapLoaderPromise, this.contextService.sharedContext.officeMarket);
        }

        if (!this.capabilitesService.capabilities.nativeSearchInHeaderEnabled) {
            await this.initializeSearchBox(main);
        }

        const searchConfig = await searchConfigPromise;

        this.initializeLoggingModule(main, searchConfig);
        this.initializeMainModule(main, searchConfig);

        await this.setSharePointSearchCenterUrl(main);

        this.eventService.broadcastEvent({
            name: AngularEvents.searchUXMainModuleLoaded,
            args: []
        });

        this.resolveSearchUXMainModule(main);

        this.eventService.broadcastEvent({
            name: AngularEvents.exitSearchInSearchBox,
            args: [main.isExitSearchInSearchBoxEnabled()]
        });
    }

    public async prepareSearch () {
        const main = await this.searchUXMainModule;
        main.prepareSearch('all');
    }

    public warmup3S() {
        return this.sharedSearchService.warmup3S(this.contextService.sharedContext.graphApiRoot);
    }

    public getStartQuery(main: Main) {
        // These checks are strictly not necessary before calling/using startQuery since it
        // already checks them and skips starting queries if not enabled. However, to fully
        // isolate the usage of startQuery we skip calling it when not enabled.
        const enableStartQuery: keyof IMainSettings = 'searchux.enableStartQuery';
        const startQueryEnabled =
            main.conf.isRegistered(enableStartQuery) && main.conf.gets(enableStartQuery);
        return startQueryEnabled ? main.startQuery : undefined;
    }

    public checkDarkModeAndRefreshTheme(event: ThemeChangeEvent): void {
        const searchConfigPromise = this.getSearchConfigPromise();
        searchConfigPromise.then(searchConf => {
            const ecsSettings = searchConf?.ecsSettings;

            const darkModeAvailable = ecsSettings?.['searchux.isDarkModeAvailable'] || false;

            this.eventService.broadcastEvent({
                name: AngularEvents.darkModeAvailable,
                args: [darkModeAvailable]
            });

            this.searchPageInstrumentationService.searchUXInstrumenter.then(instrumenter => {
                instrumenter.setProps({'darkModeAvailable': darkModeAvailable});
            });

            if (
                darkModeAvailable ||
                !event.theme?.reactShellTheme?.IsDarkMode
            ) {
                this.refreshTheme(event.theme);
            }
        });
    }

    public refreshTheme(currentTheme: Theme): void {
        const palette = currentTheme.shades.reduce(
            (theme, themeShade, index) => (
                (theme[`theme${ThemeShade[index]}`] = `${themeShade}`),
                theme
            ),
            {}
        );
        const greyScale = currentTheme.grayscaleThemeShades?.reduce(
            (theme, themeShade, index) => (
                (theme[
                    `${GrayscaleThemeShade[index]}`
                        .charAt(0)
                        .toLowerCase() +
                        `${GrayscaleThemeShade[index]}`.slice(1)
                ] = `${themeShade}`),
                theme
            ),
            {}
        );

        // to be aligned with #officeHomeRoot css in /services/OfficeHome/OfficeHome.ClientApp/less/HomePage.less
        const fonts = {
            medium: {
                fontFamily: "'SegoeUI', Helvetica, Arial, sans-serif"
            }
        };

        const themePalette = { ...palette, ...greyScale };

        loadTheme({ palette: themePalette, fonts });
    }

    // Function to know if currently in Test Environment
    private isTestEnv() {
        return !!(this.contextService.sharedContext.inMockMode ||
            (this.clientStartupConfigService.config.sharedClientStartupContext &&
                this.clientStartupConfigService.config.sharedClientStartupContext.deploymentEnvironment &&
                this.clientStartupConfigService.config.sharedClientStartupContext.deploymentEnvironment.indexOf('Ppe') > -1));
    }

    private async runPrefetchCalls(): Promise<void> {
        (await this.searchPageInstrumentationService.searchUXInstrumenter).mark('SerpPrefetchStart');
        this.warmupPersonalData();

        if (this.searchConfigService.config.fastBingAnswerResultsEnabled) {
            this.sharedSearchService.searchUXPrefetchModule.then(prefetch => prefetch.warmupMsb({
                token: undefined,
                clientType: 'OfficeSharePoint',
                msbUrl: prefetch.resolveBingUrl(this.searchPageBootstrapperService.getSearchEnvironment()),
                tokenProvider: this.bingAnswersTokenProvider
            }));
        }

        this.sharedSearchService.getSearchCenterUrl(this.contextService.sharedContext.graphApiRoot).then(searchCenterUrl => {
            this.warmup3S();

            // Personal ranking context warmup is only called when 3S is disabled
            if (!this.searchConfigService.config.fastUseSubstrateEnabled) {
                this.warmupPersonalRankingContext(searchCenterUrl);
            }
            this.searchPageInstrumentationService.searchUXInstrumenter.then(instrumenter => {
                instrumenter.mark('SerpPrefetchDone');
            });
        });
    }

    private async warmupPersonalRankingContext(searchCenterUrl: string): Promise<void> {
        this.fastSearchUXInstrumentationService.setSearchCenterUrlReferral(
            FileItem.referralFromFileLocation(searchCenterUrl));

        const prefetch = await this.sharedSearchService.searchUXPrefetchModule;

        // This is for the warmup call in getting personal ranking.
        prefetch.fetchPersonalRankingContext({
            clientId: SearchPageBootstrapperService.clientId,
            tokenProvider: this.sharedSearchService.sharePointTokenProvider,
            endpoint: `${searchCenterUrl
                ? searchCenterUrl
                : this.contextService.sharedContext.sharePointResourceId}/_api/search/query`
        });
    }

    private initialize() {
        if (!this.capabilitesService.capabilities.showSearch) {
            return;
        }

        if (!React) {
            return;
        }

        this.stringMapLoaderPromise = this.getStringMapLoader(this.contextService.sharedContext.officeMarket);
        this.initializeDispatcher();
        this.initializeInitModule();
        this.preloadSearchIfEligible();
    }

    private async initializeDispatcher(): Promise<void> {
        const dispatcher = new Dispatcher<LogEvent>();
        const dispatcherModule = await this.loadSearchDispatcher();

        this.resolveSearchUXDispatcherModule(dispatcherModule);

        dispatcherModule.setDispatcher(dispatcher);

        dispatcher.register((logEvent: LogEvent) => {
            if (logEvent.eventType !== 'USAGE') {
                return;
            }

            if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
                this.searchInstrumentationService.logSerpEvent(logEvent);
            } else {
                this.fastSearchUXInstrumentationService.searchLogger(logEvent);
            }
        });

        this.fastLogEventBufferUnregisterToken = dispatcher.register(logEvent => this.fastLogEventBuffer.push(logEvent));
    }

    private async initializeInitModule(): Promise<void> {
        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;

        merge(
            this.themingService.themeShades,
            this.routeService.getRouteChangeEndObservable().pipe(
                distinctUntilChanged()
            )
        ).subscribe((event: any) => { // had to cast to any so that I can check for event.theme and avoid lint errors
            if (event.theme) {
                this.checkDarkModeAndRefreshTheme(event);
            } else {
                const darkModeAvailable = instrumenter && instrumenter.props.darkModeAvailable;
                if (darkModeAvailable) {
                    this.refreshTheme(this.themingService.userTheme);
                }
            }
        });

        this.sharedSearchService.initializePrefetchLibrary();
    }

    private async initializeLoggingModule(
        mainModule: Main,
        searchConfiguration?: SearchConfiguration
    ): Promise<void> {
        const { puid, tenantId } = this.clientStartupConfigService.config;

        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;

        // We have never sent in searchConfiguration here prior to the EUDB changes, so we don`t require to log userFlights.
        const userFlights = [];

        const hostComponentInfo = {
            environment: undefined,
            id: undefined,
            version: undefined,
            userRing: this.searchPageInstrumentationService.getRing()
        } as any;

        const dispatcher = (await this.searchUXDispatcherModule).getDispatcher();
        const getBufferedLogEvents = (): ReadonlyArray<LogEvent> => {
            const buffer = this.fastLogEventBuffer.slice(0);
            if (this.emptyLogEventBuffer) {
                this.fastLogEventBuffer.length = 0;
                dispatcher.unregister(this.fastLogEventBufferUnregisterToken);
            }
            this.emptyLogEventBuffer = true;

            return buffer;
        };

        if (!this.contextService.sharedContext.inMockMode) {
            const test1dsLogger = searchConfiguration?.ecsSettings['searchux.test1dsLogger'] as Record<string, boolean>;
            const compare1dsLogger = searchConfiguration?.ecsSettings['searchux.compare1dsLogger'] as Record<string, boolean>;
            const loggerProps = {
                dispatcher,
                instrumenter,
                getBufferedLogEvents,
                logCtx: {
                    appInfo: {
                        id: '@1js/search-page',
                        component: 'serp'
                    },
                    userInfo: {
                        id: puid,
                        flights: userFlights
                    },
                    hostAppInfo: {
                        id: SearchPageBootstrapperService.clientId,
                        version: '',
                        userRing: this.searchPageInstrumentationService.getRing()
                    },
                    hostComponentInfo,
                    tenantInfo: {
                        omsId: tenantId
                    },
                    spInfo: {
                        siteSubscriptionId: '' // Required by interface
                    }
                },
                disableCookies: true
            };

            if (test1dsLogger) {
                mainModule.enable1dsLogger(loggerProps);
            } else {
                compare1dsLogger && mainModule.enable1dsLogger(loggerProps);
                mainModule.enableAriaLogger(loggerProps);
            }

            const useEventsLogger = searchConfiguration?.ecsSettings['searchux.enable3SEventsLogging'] as Record<string, boolean>;
            if (useEventsLogger) {
                mainModule.enable3SEventsLogger?.({
                    dispatcher,
                    instrumenter,
                    getBufferedLogEvents,
                    logCtx: {
                        userInfo: {
                            id: puid
                        },
                        hostAppInfo: {
                            id: SearchPageBootstrapperService.clientId,
                            version: '',
                            userRing: this.searchPageInstrumentationService.getRing()
                        },
                        tenantInfo: {
                            omsId: tenantId
                        }
                    },
                    tokenProvider: this.substrateTokenProvider
                });
            }
        }
    }

    private async warmupPersonalData() {
        const sharePointResourceId = this.contextService.sharedContext.sharePointResourceId;

        if (!sharePointResourceId) {
            return;
        } else if (sharePointResourceId.indexOf('office.com') !== -1) {
            const dispatcher = (await this.searchUXDispatcherModule).getDispatcher();
            dispatcher && dispatcher.dispatch({
                name: 'generic_error',
                nameDetail: 'InvalidSharePointResourceId',
                eventType: 'USAGE',
                properties: {}
            });
        }
        if (this.searchConfigService.config.fetchPersonalData) {
            const prefetch = await this.sharedSearchService.searchUXPrefetchModule;

            prefetch.fetchPersonalData({
                tokenProvider: this.sharedSearchService.sharePointTokenProvider,
                url: `${sharePointResourceId}/_api/SP.UserProfiles.PeopleManager/GetMyProperties?$select=AccountName,PersonalUrl,UserUrl`
            });
        }
    }

    private getSearchConfigPromise(): Promise<SearchConfiguration> {
        return this.isTestEnv() || this.clientStartupConfigService.config.authType === AuthType.MSA ||
        !this.searchConfigService.config.fastUseEcsSettingsOverride ? Promise.resolve(undefined) : this.fetchLokiSearchConfiguration();
    }

    private async fetchLokiSearchConfiguration(): Promise<SearchConfiguration> {
        const prefetch = await this.sharedSearchService.searchUXPrefetchModule;

        return prefetch.fetchAndParseSearchConfiguration({
            clientType: OfficeDotComOrigin,
            environment: this.searchPageBootstrapperService.getLokiEnvironment(),
            tokenProvider: this.lokiTokenProvider,
            thorTokenProvider: this.thorTokenProvider,
            useThor: this.searchConfigService.config.useThorEnabled,
            useSimpleRequest: this.searchConfigService.config.useSimpleRequest,
            additionalHeaders: {
                'X-MSSearchComponent': 'SERP'
            }
        });
    }

    private async initializeMainModule(main: Main, searchConfig?: SearchConfiguration): Promise<void> {
        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            const config: IMainSettings = {
                ...await this.mainSettingsService.createSettingsForConsumerSERP(
                    SearchPageBootstrapperService.consumerClientId,
                    this.getEnvironment(),
                    instrumenter)
            };

            if (searchConfig && searchConfig.ecsSettings && searchConfig.ecsSettings.useEcsSettings) {
                // TODO
            }

            main.conf.setsFromObject(config);

            main.exposeDebugInterface('mssearchux.officeHomeMSA', {
                version: main.version,
                conf: main.conf,
                ecsConfig: searchConfig
            });
        } else {
            let config: IMainSettings = {
                ...this.mainSettingsService.createSettings(
                    SearchPageBootstrapperService.clientId,
                    this.getEnvironment(),
                    instrumenter),
                'searchux.api.bingForBusiness.tokenProvider': this.bingAnswersTokenProvider,
                'searchux.api.sharePoint.tokenProvider': this.sharedSearchService.sharePointTokenProvider,
                'searchux.api.shareDialog.tokenProvider': this.sharedSearchService.sharePointTokenProvider,
                'searchux.api.graphMicrosoft.tokenProvider': this.graphTokenProvider,
                'searchux.api.autoSuggest.tokenProvider': this.sharedSearchService.substrateSearchServiceTokenProvider,
                'searchux.api.delegateAutoSuggest.tokenProvider': this.sharedSearchService.substrateSearchServiceTokenProvider,
                'searchux.api.loki.tokenProvider': this.lokiTokenProvider,
                'searchux.api.thor.tokenProvider': this.thorTokenProvider,
                'searchux.api.ods.tokenProvider': !this.userContextService.user.isGcc && this.odsTokenProvider,
                'searchux.api.tenantFeedback.tokenProvider': this.tenantFeedbackTokenProvider,
                'searchux.xAnchorMailbox': this.sharedSearchService.getXAnchorMailbox()
            };

            if (searchConfig && searchConfig.ecsSettings && searchConfig.ecsSettings.useEcsSettings) {
                const ecsConfig: { [key: string]: any } = { ...searchConfig.ecsSettings };
                const prefetch = await this.sharedSearchService.searchUXPrefetchModule;
                const relevantConfigIds = prefetch.getEcsIds(searchConfig.ecsConfigIds as Array<string>);
                const flights3s = config['searchux.substrateSearchClientFlights'];
                const relatedPeopleFlights = [(ecsConfig['searchux.isRelatedPeopleEnabled'] && ecsConfig['searchux.relatedPeopleClientFlight'])].filter(Boolean);

                config = {
                    ...config,
                    'searchux.api.loki.url': searchConfig.lokiUrl,
                    'searchux.userAadObjectId': searchConfig.userAadObjectId,
                    'searchux.tenantAadObjectId': searchConfig.tenantAadObjectId,
                    ...(relevantConfigIds && { 'searchux.ecsConfigIds': relevantConfigIds }),
                    // SearchConfiguration values are spread over the config object to override previous settings
                    ...ecsConfig,
                    'searchux.substrateSearchClientFlights': flights3s
                        ? flights3s.split(',').concat(relatedPeopleFlights).join(',')
                        : relatedPeopleFlights.join(',')
                };
            }

            main.conf.setsFromObject(config);

            this.setOneDriveUrl(main);

            main.exposeDebugInterface('mssearchux.officeHome', {
                version: main.version,
                conf: main.conf,
                ecsConfig: searchConfig
            });
        }
    }

    private async initializeSearchBox(main: Main) {
        this.registerSiteSuggestionProvider(main);
        this.registerFileSuggestionProvider(main);
        this.registerThirdPartyAppsSuggestionProvider(main);

        this.workloadsService.getProvisionedWorkloads()
            .then(workloads => {
                const filteredWorkloads = workloads.filter(app => !app.IsThirdParty).map(app => {
                    const appClone = { ...app };
                    if (appClone.IconUnicodeCodepointCssClass) {
                        appClone.IconUnicodeCodepointCssClass = appClone.IconUnicodeCodepointCssClass + ' ms-ohp-Icon--fast-autosuggest-app';
                    }

                    return appClone;
                });

                main.conf.sets(
                    'searchux.appWorkloads',
                    filteredWorkloads);
            });

        this.setAppsAutosuggestClick(main);
        this.setFilesAutosuggestClick(main);
        await main.registerSuggestionProviderConfigurationAsync(
            this.fastSearchBoxConfigurationName,
            this.getSuggestionProviderConfiguration());
        this.setPersonAutosuggestClick(main);
    }

    private getEnvironment() {
        return this.searchPageBootstrapperService.getSearchEnvironment() || this.searchConfigService.config.fastSearchUXEnvironment || null;
    }

    private async setSharePointSearchCenterUrl({ conf }: Main): Promise<void> {
        try {
            const searchCenterUrl = await this.sharedSearchService.getSearchCenterUrl(this.contextService.sharedContext.graphApiRoot);
            conf.sets('searchux.api.sharePointSearchCenter.url', searchCenterUrl);
        } catch (err) {
            return this.globalErrorHandler.handleError(err, ErrorId.p4CC4);
        }
    }

    private async setOneDriveUrl({ conf }: Main): Promise<void> {
        try {
            // Set OneDrive URL. If it fails or is not available, we will still try to fetch
            // it through UPA like before.
            const rootUrls = await this.oneDriveGraphService.getMyDriveRootUrl();
            const userRoot = rootUrls && rootUrls.userRoot;
            if (userRoot) {
                conf.sets('searchux.personalUrl', userRoot);
            }
        } catch (err) {
            return this.globalErrorHandler.handleError(err, ErrorId.sCtl9);
        }
    }

    private async preloadSearchIfEligible(): Promise<void> {
        if (this.clientStartupConfigService.config.authType === AuthType.MSA && this.searchConfigService.config.disableSearchEnterPressedAction) {
            return;
        }

        const profile = await this.userProfileService.get();
        const metrics = profile.metrics;

        for (const metricName in metrics) {
            // Look at user behaviors we have logged
            if (metricName.toLowerCase().indexOf('search') !== -1) {
                const metricEntry = metrics[metricName];

                const currentYear = (new Date()).getFullYear();
                const yearLastUsedSearch = new Date(metricEntry.lastOccurrence).getFullYear();

                // If they have used search in the last year, load the FAST main module.
                if (currentYear - yearLastUsedSearch <= 1) {
                    if (this.clientStartupConfigService.config.chunkLoadStrategy === 'idle-callback') {
                        this.scheduler.scheduleWork(() => {
                            this.loadMainModule();
                        }, SchedulePriority.VeryLow);
                    } else {
                        this.loadMainModule();
                    }

                    return;
                }
            }
        }
    }

    private registerSiteSuggestionProvider({ conf }: Main) {
        SitesFastSuggestionProvider.sharepointSitesService = this.sharePointSitesService;
        conf.regs('searchux.suggestionProvider.provider.officeComSitesSuggestionProvider', SitesFastSuggestionProvider);
    }

    private registerFileSuggestionProvider({ conf }: Main) {
        FilesFastSuggestionProvider.capabilitiesService = this.capabilitesService;
        FilesFastSuggestionProvider.contextService = this.contextService;
        FilesFastSuggestionProvider.deviceDetector = this.deviceDetector;
        FilesFastSuggestionProvider.mruDataService = this.mruDataService;
        FilesFastSuggestionProvider.sharedSearchService = this.sharedSearchService;
        FilesFastSuggestionProvider.secondaryDisplayTextEnabled = this.searchConfigService.config.fastAutoSuggestionFileSecondaryTextEnabled;
        // GDPRTODO: Prevent FAST from logging impressions that include local file info if windows security settings forbid it
        conf.regs('searchux.suggestionProvider.provider.officeComFilesSuggestionProvider', FilesFastSuggestionProvider);
    }

    private registerThirdPartyAppsSuggestionProvider({ conf }: Main) {
        ThirdPartyAppsFastSuggestionProvider.workloadsService = this.workloadsService;
        conf.regs('searchux.suggestionProvider.provider.officeComThirdPartyAppsSuggestionProvider', ThirdPartyAppsFastSuggestionProvider);
    }

    private setPersonAutosuggestClick({ conf }: Main) {
        const componentSettingName = 'searchux.suggestionComponent.person.default';
        const oldComponent = conf.gets(componentSettingName);
        // The lpc wraps around this hidden element to show a people card for people autosuggestions
        const peopleCardContainer = document.createElement('div');
        document.body.appendChild(peopleCardContainer);
        const _this = this;

        conf.sets(
            componentSettingName,
            props =>
                React.createElement(oldComponent as React.ComponentClass, (Object as any).assign({}, props, {
                    onClick(e) {
                        e.preventDefault();
                        _this.sharedSearchService.setPersonAutosuggestClick(props, peopleCardContainer);
                    }
                }))
        );
    }

    private setFilesAutosuggestClick({ conf }: Main) {
        const componentSettingName = 'searchux.suggestionComponent.file.default';
        const oldComponent = conf.gets(componentSettingName);
        const _this = this;

        conf.sets(
            componentSettingName,
            props =>
                React.createElement(oldComponent as React.ComponentClass, (Object as any).assign({}, props, {
                    onClick(e) {
                        e.preventDefault();
                        _this.sharedSearchService.setFilesAutosuggestClick(props);
                    }
                }))
        );
    }

    // Used for logging the app referral.
    // Also, if a user clicks on a third party app, we want to update the recently used apps
    private setAppsAutosuggestClick({ conf }: Main) {
        let thirdPartyApps = new Array<Workload>();
        this.workloadsService.getThirdPartyWorkloads().subscribe(workloads => thirdPartyApps = workloads);
        const componentSettingName = 'searchux.suggestionComponent.app.default';
        const oldComponent = conf.gets(componentSettingName);
        const _this = this;

        conf.sets(
            componentSettingName,
            props =>
                React.createElement(oldComponent as React.ComponentClass, (Object as any).assign({}, props, {
                    onClick(e) {
                        e.preventDefault();
                        _this.sharedSearchService.setAppsAutosuggestClick(props, { ...e }, thirdPartyApps);
                    }
                }))
        );
    }

    private getSuggestionProviderConfiguration(): ISerializedSuggestionProviderConfiguration {
        return {
            streamPolicy: 'searchux.suggestionProvider.streamPolicy.ordered',
            groups: [
                {
                    id: 'app',
                    optimalCount: 1,
                    providers: [
                        'searchux.suggestionProvider.provider.o365Apps', 'searchux.suggestionProvider.provider.officeComThirdPartyAppsSuggestionProvider'
                    ],
                    component: 'searchux.suggestionComponent.app.default',
                    heading: 'searchux.strings.suggestionGroup.app.heading',
                    ariaLabel: 'searchux.strings.suggestionGroup.app.ariaLabel'
                },
                {
                    id: 'file',
                    optimalCount: 3,
                    providers: this.capabilitesService.capabilities.isPwaInUwp ? [
                        'searchux.suggestionProvider.provider.autosuggestfile', 'searchux.suggestionProvider.provider.officeComFilesSuggestionProvider'
                    ] : [
                            'searchux.suggestionProvider.provider.autosuggestfile'
                        ],
                    component: 'searchux.suggestionComponent.file.default',
                    heading: 'searchux.strings.suggestionGroup.file.heading',
                    ariaLabel: 'searchux.strings.suggestionGroup.file.ariaLabel'
                },
                {
                    id: 'person',
                    optimalCount: 2,
                    providers: [
                        'searchux.suggestionProvider.provider.autosuggestpeople'
                    ],
                    component: 'searchux.suggestionComponent.person.default',
                    heading: 'searchux.strings.suggestionGroup.person.heading',
                    ariaLabel: 'searchux.strings.suggestionGroup.person.ariaLabel'
                },
                {
                    id: 'site',
                    optimalCount: 2,
                    providers: [
                        'searchux.suggestionProvider.provider.autosuggestsite'
                    ],
                    component: 'searchux.suggestionComponent.site.default',
                    heading: 'searchux.strings.suggestionGroup.site.heading',
                    ariaLabel: 'searchux.strings.suggestionGroup.site.ariaLabel'
                }
            ]
        };
    }

    private async loadMain(): Promise<Main> {
        const searchPageModule = await this.searchPageBootstrapperService.searchPageModule;

        return await searchPageModule.loadMain();
    }

    private async loadSearchDispatcher(): Promise<SearchDispatcher> {
        const searchPageModule = await this.searchPageBootstrapperService.searchPageModule;

        return await searchPageModule.loadSearchDispatcher();
    }

    private async getStringMapLoader(prefetchLocale?: string): Promise<StringMapLoader> {
        const searchPageModule = await this.searchPageBootstrapperService.searchPageModule;
        const searchStrings = await searchPageModule.loadStrings();
        const stringMapLoaderPromise = searchStrings.getStringMapLoader(prefetchLocale, this.searchPageBootstrapperService.cdnOptions);

        return stringMapLoaderPromise;
    }
}
